import { getProductPricingRequirements } from "./productConfigs";

export const hasManualFabricRate = row =>
  ["manual_quote", "manual_override", "quote_manual"].includes(row.rrpSource)
  || (row.rateSource === "manual_override" && (row.rateField || "mrp") === "mrp") || row.rateSources?.mrp === "manual_override";

export const catalogueRateField = row => row.cataloguePricing?.field || row.catalogueRateField ||
  ((row.productConfigSnapshot?.calculationType || row.calculationType) === "sqft_only" ? "sqftRate" : "mrp");

export const hasManualCatalogueRate = (row, field = catalogueRateField(row)) =>
  row.rateSources?.[field] === "manual_override" ||
  (row.rateSource === "manual_override" && (row.rateField || "mrp") === field) ||
  (field === "mrp" && ["manual_quote", "manual_override", "quote_manual"].includes(row.rrpSource));

// Catalogue units are explicit. Legacy catalogues are meters, never inferred sqft.
export function buildCatalogueRatePatch(row, resolution = {}) {
  const type = row.productConfigSnapshot?.calculationType || row.calculationType || "fabric_meter";
  const unit = resolution.priceUnit || resolution.catalogue?.priceUnit || "m";
  const field = unit === "sqft" ? "sqftRate" : "mrp";
  const compatible = unit === "sqft" ? ["sqft_only", "sqft_plus_fabric"].includes(type)
    : unit === "m" && ["fabric_meter", "sqft_plus_fabric"].includes(type);
  if (!compatible) return { cataloguePricingError: `Catalogue price is per ${unit}. Select a compatible catalogue price or enter the required rate manually.` };
  const metadata = { cataloguePriceUnit: unit, catalogueRateField: field, cataloguePricingError: "" };
  if (hasManualCatalogueRate(row, field)) return metadata;
  const numeric = resolution.rrpPerMeter;
  const matched = resolution.matched === true && numeric != null && numeric !== "" && Number.isFinite(Number(numeric)) && Number(numeric) >= 0;
  const rate = matched ? String(numeric) : "";
  const source = matched ? resolution.rrpSource : "unmatched";
  const provenance = { field, unit, source, ruleId: resolution.rule?.id || "", rate: matched ? Number(numeric) : null,
    brandId: resolution.brandId || row.brandId || row.fabricBrandId || "",
    catalogueId: resolution.catalogue?.id || row.catalogueId || "",
    srNumber: resolution.parsed?.srNumber ?? row.srNumber ?? "" };
  return { ...metadata, [field]: rate,
    ...(field === "mrp" ? { mrpPerMeter: rate, ratePerMeter: rate, rrpPerMeter: matched ? Number(numeric) : null, rrpSource: source }
      : { ratePerSqft: rate }),
    rateSources: { ...(row.rateSources || {}), [field]: "catalogue" },
    ...(field === "sqftRate" || type === "fabric_meter" ? { rateSource: "catalogue", rateField: field, rateOverridden: false } : {}),
    cataloguePricing: provenance,
    catalogueRates: { ...(row.catalogueRates || {}), [field]: provenance },
  };
}

export const srDisplayLabel = value => {
  const text = String(value ?? "").trim();
  return /^\d+$/.test(text) ? `SR-${text}` : text;
};

export const isAutomaticFabricRate = row => row.rateSource === "catalogue" || row.rateSources?.[catalogueRateField(row)] === "catalogue"
  || ["sr_price_rule", "catalogue_default", "needs_sr_number"].includes(row.rrpSource);
const hasUnresolvedApplicablePricing = row => (Boolean(row.cataloguePricingError) &&
  !hasManualCatalogueRate(row, (row.productConfigSnapshot?.calculationType || row.calculationType) === "sqft_only" ? "sqftRate" : "mrp")) ||
  Object.entries(row.catalogueRates || {}).some(([field, provenance]) => !hasManualCatalogueRate(row, field)
    && row.rateSources?.[field] === "catalogue" && ["unmatched", "needs_sr_number"].includes(provenance?.source)) ||
  (!hasManualCatalogueRate(row) && isAutomaticFabricRate(row)
    && ["unmatched", "needs_sr_number"].includes(row.cataloguePricing?.source || row.rrpSource));

export const hasUnresolvedFabricPricing = row => {
  const config = row.productConfigSnapshot || { calculationType: row.calculationType };
  const requirements = getProductPricingRequirements(config);
  if (requirements.requiresSqftRate && !requirements.usesSeparateFabricCost) {
    // A previous fabric selection is not a price source for an area-only product.
    const applicable = { ...row, catalogueRates: row.catalogueRates?.sqftRate ? { sqftRate: row.catalogueRates.sqftRate } : {},
      cataloguePricing: row.cataloguePricing?.field === "sqftRate" ? row.cataloguePricing : null,
      rrpSource: "not_applicable", rateSources: { sqftRate: row.rateSources?.sqftRate },
      rateSource: row.rateField === "mrp" ? "product_default" : row.rateSource,
      cataloguePricingError: row.catalogueRateField === "mrp" || row.cataloguePricing?.field === "mrp" ? "" : row.cataloguePricingError };
    return hasUnresolvedApplicablePricing(applicable);
  }
  return hasUnresolvedApplicablePricing(row);
};

const invalidateAutomaticRate = row => {
  if (hasManualCatalogueRate(row)) return {};
  if (!isAutomaticFabricRate(row)) return {};
  if (catalogueRateField(row) === "sqftRate") return { sqftRate: "", ratePerSqft: "",
    ...(row.catalogueRates?.sqftRate ? { catalogueRates: { ...row.catalogueRates, sqftRate: { ...row.catalogueRates.sqftRate, source: "unmatched", rate: null, ruleId: "" } } } : {}),
    cataloguePricing: { ...row.cataloguePricing, field: "sqftRate", unit: "sqft", source: "unmatched", rate: null, ruleId: "" },
    srPriceRuleId: "", srPriceRuleType: "none", srPriceRuleLabel: "" };
  return { mrp: "", mrpPerMeter: "", ratePerMeter: "", rrpPerMeter: null,
    ...(row.catalogueRates?.mrp ? { catalogueRates: { ...row.catalogueRates, mrp: { ...row.catalogueRates.mrp, source: "unmatched", rate: null, ruleId: "" } } } : {}),
    ...(row.cataloguePricing ? { cataloguePricing: { ...row.cataloguePricing, source: "unmatched", rate: null, ruleId: "" } } : {}),
    rrpSource: "unmatched", ...((row.productConfigSnapshot?.calculationType || row.calculationType) !== "sqft_plus_fabric" ? { rateSource: "catalogue" } : {}), rateSources: { ...row.rateSources, mrp: "catalogue" }, srPriceRuleId: "", srPriceRuleType: "none", srPriceRuleLabel: "" };
};

export function buildSrDraftPatch(row, value) {
  const srNumber = String(value ?? "");
  // Exact identifiers are display data; legacy grouping keys are resolved separately.
  const text = srNumber.trim();
  const storedLabel = srDisplayLabel(text);
  const fabric = row.catalogueName ? `${row.catalogueName} ${storedLabel}`.trimEnd() : srNumber;
  return { ...invalidateAutomaticRate(row), srNumber, srLabel: srDisplayLabel(srNumber),
    fabric, fabricName: fabric, fabricCode: fabric, fabricCodeRaw: fabric,
    ...(srNumber !== String(row.srNumber ?? "") ? { selectedFabricRef: null } : {}) };
}

export function buildCatalogueInputDraftPatch(row, value) {
  const text = String(value ?? "");
  const patch = { ...buildManualFabricDraftPatch(row, text), catalogueName: text };
  if ((row.productConfigSnapshot?.calculationType || row.calculationType) === "sqft_plus_fabric") {
    // Search text does not identify whether the next selection is meters or sqft.
    // Keep both components until an explicitly unit-tagged selection is made.
    for (const field of ["mrp", "mrpPerMeter", "ratePerMeter", "rrpPerMeter", "sqftRate", "ratePerSqft", "rrpSource", "rateSource", "rateField", "rateSources", "cataloguePricing", "catalogueRates", "catalogueRateField", "cataloguePriceUnit"]) {
      if (row[field] !== undefined) patch[field] = row[field];
    }
    patch.cataloguePricingError = "Select a catalogue price or enter the required rates manually.";
  }
  return patch;
}

export function buildManualFabricDraftPatch(row, value) {
  const text = String(value ?? "");
  return { ...invalidateAutomaticRate(row), fabric: text, fabricName: text, fabricCode: text, fabricCodeRaw: text,
    catalogueId: "", catalogueName: "", catalogueKey: "", srNumber: "", srLabel: "",
    srPriceRuleId: "", srPriceRuleType: "none", srPriceRuleLabel: "", selectedFabricRef: null,
    fabricSource: text ? "quote_manual" : "none" };
}

export function clearCatalogueSelection(row) {
  return { ...buildManualFabricDraftPatch(row, row.srNumber || row.fabricCodeRaw || ""),
    srNumber: String(row.srNumber ?? ""), srLabel: srDisplayLabel(row.srNumber) };
}

// Preserve ordinary legacy keys. Encode meaningful punctuation losslessly so
// AB/103, AB-103 and AB103 cannot collapse into the same summary/optimizer pool.
export function fabricGroupingKey(value) {
  const text = String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  const withoutDisplayPrefix = text.replace(/\bsr-(?=\S)/g, "sr");
  if (/^[a-z0-9 ]*$/.test(withoutDisplayPrefix)) return withoutDisplayPrefix;
  return `fqid${Array.from(withoutDisplayPrefix).map(char => char.codePointAt(0).toString(16).padStart(6, "0")).join("")}`;
}
